import { EventListener } from "silver";
import { toggleChildNodeAvailability } from "shared/functions";

/**
 * Handles slide animations for `slider` blocks.
 * See the `_slider.scss` BEM file
 */

const CLASS_PREVIOUS = "-prev";
const CLASS_NEXT = "-next";
const CLASS_HIDDEN = "-hidden";
const CLASS_SLIDE = "slider_item";
const CLASS_ROOT = "slider";

export class SliderBlock {
	public onSlide = EventListener.create("slide");
	private lastIndex: number = -1;

	constructor(
		private root: HTMLElement,
		private slides: HTMLElement[],
		private idToIndex: Record<string, number> = { }
	) {
		root.classList.add(CLASS_ROOT);
		root.setAttribute("aria-live", "polite");
		root.setAttribute("role", "region");

		slides.forEach(el => {
			el.classList.add(CLASS_SLIDE, CLASS_HIDDEN);
			el.setAttribute("aria-roledescription", "slide");
			el.setAttribute("role", "group");
		});

		window.addEventListener("resize", () => this.resize());
	}

	public get length(): number {
		return this.slides.length;
	}

	public get index(): number {
		return this.lastIndex;
	}

	public static makeFromAttribute(root: HTMLElement, attribute: string) {
		const slides = Array.from(root.querySelectorAll("[" + attribute + "]") as NodeListOf<HTMLElement>);
		const index = slides.reduce<Record<string, number>>((agg, el, i: number) => {
			agg[String(el.getAttribute(attribute))] = i;
			return agg;
		}, { });
		return new SliderBlock(root, slides, index);
	}


	private toggleSlideA11yVisibility(slide: HTMLElement, visible: boolean): void {
		slide.setAttribute("aria-hidden", visible ? "false" : "true");
		toggleChildNodeAvailability(slide, visible);
	}

	/**
	 * Slides the `slide` block in the desired direction off the page
	 */
	private slideOff(index: number, directionClass: string): Promise<void> {
		const element = this.slides[index];
		element.classList.add(directionClass);
		this.toggleSlideA11yVisibility(element, false);
		return new Promise((resolve) => {
			setTimeout(() => {
				element.classList.add(CLASS_HIDDEN);
				element.classList.remove(directionClass);
				resolve();
			}, 300);
		});
	}

	/**
	 * Applies the directional class to the target element. Timing makes things complicated.
	 */
	private startSlideOn(index: number, directionClass: string): void {
		const element = this.slides[index];
		element.classList.add(directionClass);
		this.toggleSlideA11yVisibility(element, true);
	}

	/**
	 * Removes the directional class to trigger the animation
	 */
	private finishSlideOn(index: number, directionClass: string): Promise<void> {
		const element = this.slides[index];
		return new Promise((resolve) => {
			element.classList.remove(CLASS_HIDDEN);
			setTimeout(() => {
				element.classList.remove(directionClass);
				setTimeout(resolve, 300);
			}, 1);
		});
	}

	private resize(): void {
		if (this.lastIndex === -1) {
			return;
		}
		const { height } = this.slides[this.lastIndex].getBoundingClientRect();
		this.root.style.height = `${height}px`;
	}

	public async showSlide(index: number): Promise<void> {
		if (index < 0 || index >= this.slides.length) {
			throw new Error("Index out of range");
		}
		if (index === this.lastIndex) {
			this.resize();
			return;
		}
		const directionClass = index < this.lastIndex ? CLASS_PREVIOUS : CLASS_NEXT;
		let waitFor = Promise.resolve();

		if (this.lastIndex >= 0) {
			waitFor = this.slideOff(this.lastIndex, directionClass == CLASS_NEXT ? CLASS_PREVIOUS : CLASS_NEXT);
		}

		this.lastIndex = index;
		this.resize();
		this.startSlideOn(index, directionClass);
		await waitFor;
		this.onSlide.trigger({ });
		return this.finishSlideOn(index, directionClass);
	}

	public reset(): void {
		this.slides.forEach(slide => {
			this.toggleSlideA11yVisibility(slide, false);
			slide.classList.remove(CLASS_PREVIOUS, CLASS_NEXT);
			slide.classList.add(CLASS_HIDDEN);
		});
		this.lastIndex = -1;
	}

	public async showNextSlide(): Promise<void> {
		return this.showSlide(this.lastIndex + 1);
	}

	/**
	 * Animates in the sliding element and animates out any previous element
	 */
	public async showSlideFromId(id: string): Promise<void> {
		if (!this.idToIndex.hasOwnProperty(id)) {
			throw new Error("Unknown slide id");
		}
		return this.showSlide(this.idToIndex[id]);
	}
}
